import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

function FullPageNotice({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR'] flex items-center justify-center px-6">
      <div className="text-center max-w-[420px]">{children}</div>
    </div>
  );
}

/**
 * 로그인(그리고 선택적으로 관리자) 게이트.
 *
 * 세션 복원과 admin 조회는 비동기다. **끝나기 전에 판단하면** 새로고침할 때마다
 * 로그인한 사용자가 /login 으로 튕긴다 — loading/adminChecked 를 반드시 기다린다.
 */
export default function RequireAuth({
  children,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}) {
  const { user, loading, isAdmin, adminChecked } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <FullPageNotice>
        <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
      </FullPageNotice>
    );
  }

  if (!user) {
    // 로그인 후 원래 가려던 곳으로 돌아오게 경로를 실어 보낸다.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requireAdmin) {
    if (!adminChecked) {
      return (
        <FullPageNotice>
          <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
        </FullPageNotice>
      );
    }
    if (!isAdmin) {
      return (
        <FullPageNotice>
          <i className="ri-lock-line text-4xl text-[#B8B3AE] mb-4 block"></i>
          <h2 className="text-lg font-bold text-[#1C1917] mb-2">관리자 전용 페이지입니다</h2>
          <p className="text-sm text-[#8C8780]">
            접근 권한이 없습니다. 권한이 필요하면 운영자에게 문의해 주세요.
          </p>
        </FullPageNotice>
      );
    }
  }

  return <>{children}</>;
}
